import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Table from '@src/components/NewTable';
import actions from '@src/redux/actions';
import ROUTES from '@src/constants/route';
import ProjectRow from './ProjectRow';
import RowActions from './RowActions';
import FailureWarning from './FailureWarning';
import useRecentProjects from './useRecentProjects';
import { StyledRowActions } from './index.style';

const ROWS_PER_PAGE_OPTIONS = [10, 25];

// 52 of content, 11 of cell padding, 1 of border.
const ROW_HEIGHT = 64;

const ProjectList = ({
  search,
  removedIds,
  selected,
  onSelect,
  onPageItems,
  onDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    projects,
    total,
    loading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useRecentProjects({
    search,
    removedIds,
    defaultRowsPerPage: ROWS_PER_PAGE_OPTIONS[0],
  });

  // As tall as its rows, up to a default page: ten fit without scrolling, and a
  // shorter page leaves no empty box under it.
  const rows = Math.min(projects.length, ROWS_PER_PAGE_OPTIONS[0]);
  const tableHeight = rows ? `${rows * ROW_HEIGHT}px` : undefined;

  // Only one row menu at a time, so it is held here rather than in each row.
  const [openMenuId, setOpenMenuId] = useState(null);

  // The selection bar lives in the toolbar above, so it needs this page's rows.
  useEffect(() => onPageItems(projects), [projects]);

  // The checkbox stops the click, so ClickAwayListener never hears it.
  const handleSelect = (next) => {
    setOpenMenuId(null);
    onSelect(next);
  };

  // The detail page picks its layout from project.product.
  const handleOpenProject = (id) => {
    const project = projects.find((item) => item.id === id);
    // A failed project has no content to show.
    if (project?.failed) return;
    dispatch(actions.project.setProduct(project?.product));
    history.push(`${ROUTES.PROJECTS}/${id}`);
  };

  const columns = [
    {
      id: 'title',
      field: 'title',
      align: 'left',
      width: '100%',
      cellWidth: '100%',
      render: (row) => <ProjectRow project={row} />,
    },
    {
      id: 'actions',
      field: 'actions',
      align: 'right',
      render: (row) => (
        <StyledRowActions>
          {row?.failed && <FailureWarning placement="left" />}
          <RowActions
            open={openMenuId === row.id}
            onOpenChange={(next) => setOpenMenuId(next ? row.id : null)}
            onDelete={() => onDelete(row)}
          />
        </StyledRowActions>
      ),
    },
  ];

  return (
    <Table
      // Without this the table renders at height 0.
      isExpand
      columns={columns}
      data={projects}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      heightTableProjects={tableHeight}
      footerPadding="0px 20px 0px"
      onChangePage={setPage}
      onChangeRowPage={setRowsPerPage}
      onRowClick={handleOpenProject}
      loading={loading}
      selected={selected}
      onSelect={handleSelect}
      noDataText={t('projectListEmpty')}
    />
  );
};

// Typing in the search box re-renders the section on every keystroke, and every
// prop here holds still until the keyword settles.
export default React.memo(ProjectList);
